/**
 * @module services/catalogs/sudocService
 * @description Service for querying the Sudoc catalog via SRU protocol
 *
 * The Sudoc (Système universitaire de documentation) is the French academic
 * union catalog. This service uses the SRU (Search/Retrieve via URL) protocol
 * to search and retrieve bibliographic records in UNIMARC format.
 *
 * @see https://www.sudoc.abes.fr/cbs/sru/
 */

import type { ItemData, ItemType, Creator, CreatorType } from '@types';
import type { BaseCatalogRecord, SRUSearchOptions } from './types';
import { CATALOG_PROXY_PATHS } from './types';
import { SRUBaseService } from './sruBase';
import { cleanUnimarcText, mapLanguageCode, capitalizePublisher } from './utils';

// =============================================================================
// TYPES
// =============================================================================

/** Sudoc search result */
export interface SudocRecord extends BaseCatalogRecord {
  /** PPN (Pica Production Number) - unique identifier */
  ppn: string;
  /** Raw UNIMARC record (for debugging) */
  rawXml?: string;
}

/** Sudoc search response */
export interface SudocSearchResponse {
  totalResults: number;
  records: SudocRecord[];
}

/** Search index options */
export type SudocSearchIndex =
  | 'tou' // All words
  | 'mti' // Title words
  | 'aut' // Author words
  | 'isb' // ISBN
  | 'isn' // ISSN
  | 'ppn' // PPN (record ID)
  | 'msu' // Subject words
  | 'edi' // Publisher
  | 'nnt'; // National thesis number

/** Document type codes */
export type SudocDocumentType =
  | 'b' // Monographies (books)
  | 't' // Periodicals
  | 'y' // Theses
  | 'a' // Articles
  | 'o' // E-books
  | 'f' // Manuscripts
  | 'k' // Maps
  | 'v'; // Audio-visual

/** Sudoc search options */
export interface SudocSearchOptions extends SRUSearchOptions {
  index?: SudocSearchIndex;
  documentType?: SudocDocumentType;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Map UNIMARC function codes to Zotero creator types */
const CREATOR_FUNCTION_MAP: Record<string, CreatorType> = {
  '070': 'author', // Author
  '080': 'author', // Author (alternative)
  '090': 'author', // Author
  '340': 'editor', // Editor
  '440': 'translator', // Translator
  '651': 'director', // Director (thesis)
  '727': 'director', // Director of research
  '730': 'translator', // Translator
};

// =============================================================================
// SUDOC SERVICE
// =============================================================================

class SudocService extends SRUBaseService<SudocRecord, SudocSearchOptions> {
  protected readonly serviceName = 'Sudoc';
  protected readonly proxyPath = CATALOG_PROXY_PATHS.sudoc;
  protected readonly sruVersion = '1.1';
  protected readonly defaultRecordSchema = 'unimarc';

  /**
   * Search the Sudoc catalog
   */
  async search(query: string, options: SudocSearchOptions = {}): Promise<SudocSearchResponse> {
    const { index = 'tou', documentType, startRecord = 1, maximumRecords = 10 } = options;

    // Build query
    let sruQuery = `${index}=${query}`;

    // Add document type filter if specified
    if (documentType) {
      sruQuery += ` and tdo=${documentType}`;
    }

    return this.executeSearch(sruQuery, {
      startRecord,
      maximumRecords,
    }) as Promise<SudocSearchResponse>;
  }

  /**
   * Get a single record by PPN
   */
  async getByPPN(ppn: string): Promise<SudocRecord | null> {
    const result = await this.search(ppn, { index: 'ppn', maximumRecords: 1 });
    return result.records[0] || null;
  }

  /**
   * Parse recordData element containing UNIMARC record
   */
  protected parseRecordData(recordDataEl: Element): SudocRecord | null {
    const unimarcRecord = this.findElementByLocalName(recordDataEl, 'record');
    if (!unimarcRecord) {
      return null;
    }
    return this.parseUnimarcRecord(unimarcRecord);
  }

  /**
   * Parse a single UNIMARC record to Zotero format
   */
  private parseUnimarcRecord(recordEl: Element): SudocRecord | null {
    const item: Partial<ItemData> = {
      creators: [],
    };

    // Get PPN from 001 field
    const ppn = this.getControlField(recordEl, '001');
    if (!ppn) {
      return null;
    }

    // Get leader for document type
    const leader = recordEl.querySelector('leader')?.textContent || '';
    const docTypeCode = leader.length >= 8 ? leader.charAt(6) + leader.charAt(7) : 'am';

    // Map document type
    item.itemType = this.mapDocumentType(docTypeCode, recordEl);

    // Parse data fields
    this.parseTitle(recordEl, item);
    this.parseCreators(recordEl, item);
    this.parsePublicationInfo(recordEl, item);
    this.parseIdentifiers(recordEl, item);
    this.parseSubjects(recordEl, item);
    this.parsePhysicalDescription(recordEl, item);
    this.parseNotes(recordEl, item);
    this.parseThesisInfo(recordEl, item);
    this.parseSeriesInfo(recordEl, item);
    this.parseLanguage(recordEl, item);

    // Validate creator types and fields
    this.validateItem(item);

    return {
      id: ppn,
      ppn,
      item,
      rawXml: recordEl.outerHTML,
    };
  }

  // ==========================================================================
  // UNIMARC Field Helpers
  // ==========================================================================

  private getControlField(recordEl: Element, tag: string): string | null {
    const field = recordEl.querySelector(`controlfield[tag="${tag}"]`);
    return cleanUnimarcText(field?.textContent);
  }

  private getSubfield(fieldEl: Element, code: string): string | null {
    const subfield = fieldEl.querySelector(`subfield[code="${code}"]`);
    return cleanUnimarcText(subfield?.textContent);
  }

  private getSubfields(fieldEl: Element, code: string): string[] {
    const subfields = fieldEl.querySelectorAll(`subfield[code="${code}"]`);
    return Array.from(subfields)
      .map((sf) => cleanUnimarcText(sf.textContent))
      .filter((text): text is string => text !== null);
  }

  private getDataFields(recordEl: Element, tag: string): Element[] {
    return Array.from(recordEl.querySelectorAll(`datafield[tag="${tag}"]`));
  }

  // ==========================================================================
  // Document Type Mapping
  // ==========================================================================

  private mapDocumentType(code: string, recordEl: Element): ItemType {
    const recordType = code.charAt(0).toLowerCase();
    const bibLevel = code.charAt(1).toLowerCase();

    // Check for thesis indicator
    const thesisField = recordEl.querySelector('datafield[tag="029"], datafield[tag="328"]');
    if (thesisField) {
      return 'thesis';
    }

    // Map based on bibliographic level
    switch (bibLevel) {
      case 'm': // Monograph
        switch (recordType) {
          case 'g':
            return 'videoRecording';
          case 'i':
            return 'audioRecording';
          case 'j':
            return 'audioRecording';
          case 'e':
            return 'map';
          case 'f':
            return 'map';
          case 'k':
            return 'artwork';
          case 'l':
            return 'computerProgram';
          case 'c':
            return 'audioRecording';
          case 'd':
            return 'manuscript';
          default:
            return 'book';
        }
      case 'a':
      case 'b':
        return 'journalArticle';
      case 's':
        return 'journalArticle';
      case 'c':
        return 'book';
      default:
        switch (recordType) {
          case 'g':
            return 'videoRecording';
          case 'i':
            return 'audioRecording';
          case 'j':
            return 'audioRecording';
          case 'e':
            return 'map';
          case 'k':
            return 'artwork';
          default:
            return 'book';
        }
    }
  }

  // ==========================================================================
  // Field Parsers
  // ==========================================================================

  private parseTitle(recordEl: Element, item: Partial<ItemData>): void {
    const field200 = recordEl.querySelector('datafield[tag="200"]');
    if (field200) {
      const mainTitle = this.getSubfield(field200, 'a');
      const subtitle = this.getSubfield(field200, 'e');
      if (mainTitle) {
        item.title = subtitle ? `${mainTitle}: ${subtitle}` : mainTitle;
      }
    }

    const field500 = recordEl.querySelector('datafield[tag="500"]');
    if (field500 && !item.title) {
      item.title = this.getSubfield(field500, 'a') || '';
    }

    const field532 = recordEl.querySelector('datafield[tag="532"]');
    if (field532) {
      item.shortTitle = this.getSubfield(field532, 'a') || undefined;
    }
  }

  private parseCreators(recordEl: Element, item: Partial<ItemData>): void {
    const creators: Creator[] = [];

    // Personal names (700, 701, 702)
    const personalFields = [
      ...this.getDataFields(recordEl, '700'),
      ...this.getDataFields(recordEl, '701'),
      ...this.getDataFields(recordEl, '702'),
    ];

    for (const field of personalFields) {
      const lastName = this.getSubfield(field, 'a');
      const firstName = this.getSubfield(field, 'b');
      const functionCode = this.getSubfield(field, '4') || '070';

      if (lastName) {
        creators.push({
          creatorType: CREATOR_FUNCTION_MAP[functionCode] || 'author',
          lastName,
          firstName: firstName || undefined,
        });
      }
    }

    // Corporate names (710, 711, 712)
    const corporateFields = [
      ...this.getDataFields(recordEl, '710'),
      ...this.getDataFields(recordEl, '711'),
      ...this.getDataFields(recordEl, '712'),
    ];

    for (const field of corporateFields) {
      const name = this.getSubfield(field, 'a');
      const subdivision = this.getSubfield(field, 'b');
      const functionCode = this.getSubfield(field, '4') || '070';

      if (name) {
        creators.push({
          creatorType: CREATOR_FUNCTION_MAP[functionCode] || 'author',
          name: subdivision ? `${name}. ${subdivision}` : name,
        });
      }
    }

    item.creators = creators;
  }

  private parsePublicationInfo(recordEl: Element, item: Partial<ItemData>): void {
    const pubField =
      recordEl.querySelector('datafield[tag="214"]') ||
      recordEl.querySelector('datafield[tag="210"]');

    if (pubField) {
      const place = this.getSubfield(pubField, 'a');
      const publisher = this.getSubfield(pubField, 'c');
      if (place) item.place = capitalizePublisher(place);
      if (publisher) item.publisher = capitalizePublisher(publisher);

      const date = this.getSubfield(pubField, 'd');
      if (date) {
        const yearMatch = date.match(/\d{4}/);
        item.date = yearMatch ? yearMatch[0] : date;
      }
    }
  }

  private parseIdentifiers(recordEl: Element, item: Partial<ItemData>): void {
    const field010 = recordEl.querySelector('datafield[tag="010"]');
    if (field010) {
      item.ISBN = this.getSubfield(field010, 'a') || undefined;
    }

    const field011 = recordEl.querySelector('datafield[tag="011"]');
    if (field011) {
      item.ISSN = this.getSubfield(field011, 'a') || undefined;
    }

    const field017 = recordEl.querySelector('datafield[tag="017"]');
    if (field017) {
      const doi = this.getSubfield(field017, 'a');
      if (doi) {
        item.DOI = doi;
      }
    }

    const field856 = recordEl.querySelector('datafield[tag="856"]');
    if (field856) {
      item.url = this.getSubfield(field856, 'u') || undefined;
    }
  }

  private parseSubjects(recordEl: Element, item: Partial<ItemData>): void {
    const subjects: string[] = [];

    for (const field of this.getDataFields(recordEl, '600')) {
      const name = this.getSubfield(field, 'a');
      if (name) subjects.push(name);
    }

    for (const field of this.getDataFields(recordEl, '601')) {
      const name = this.getSubfield(field, 'a');
      if (name) subjects.push(name);
    }

    for (const field of this.getDataFields(recordEl, '606')) {
      const subject = this.getSubfield(field, 'a');
      if (subject) subjects.push(subject);
    }

    for (const field of this.getDataFields(recordEl, '607')) {
      const place = this.getSubfield(field, 'a');
      if (place) subjects.push(place);
    }

    for (const field of this.getDataFields(recordEl, '610')) {
      const subject = this.getSubfield(field, 'a');
      if (subject) subjects.push(subject);
    }

    if (subjects.length > 0) {
      const existingExtra = item.extra || '';
      const subjectsStr = `Subjects: ${subjects.join('; ')}`;
      item.extra = existingExtra ? `${existingExtra}\n${subjectsStr}` : subjectsStr;
    }
  }

  private parsePhysicalDescription(recordEl: Element, item: Partial<ItemData>): void {
    const field215 = recordEl.querySelector('datafield[tag="215"]');
    if (field215) {
      const pages = this.getSubfield(field215, 'a');
      if (pages) {
        const pageMatch = pages.match(/(\d+)\s*p/i);
        if (pageMatch) {
          item.numPages = pageMatch[1];
        }
      }
    }
  }

  private parseNotes(recordEl: Element, item: Partial<ItemData>): void {
    const field300 = recordEl.querySelector('datafield[tag="300"]');
    if (field300) {
      const note = this.getSubfield(field300, 'a');
      if (note) {
        const existingExtra = item.extra || '';
        item.extra = existingExtra ? `${existingExtra}\n${note}` : note;
      }
    }

    const field330 = recordEl.querySelector('datafield[tag="330"]');
    if (field330) {
      item.abstractNote = this.getSubfield(field330, 'a') || undefined;
    }
  }

  private parseThesisInfo(recordEl: Element, item: Partial<ItemData>): void {
    const field328 = recordEl.querySelector('datafield[tag="328"]');
    if (field328) {
      item.thesisType = this.getSubfield(field328, 'b') || 'Thèse';
      item.university = this.getSubfield(field328, 'e') || undefined;

      const year = this.getSubfield(field328, 'd');
      if (year && !item.date) {
        item.date = year;
      }
    }

    const field029 = recordEl.querySelector('datafield[tag="029"]');
    if (field029) {
      const nnt = this.getSubfield(field029, 'b');
      if (nnt) {
        const existingExtra = item.extra || '';
        const nntStr = `NNT: ${nnt}`;
        item.extra = existingExtra ? `${existingExtra}\n${nntStr}` : nntStr;
      }
    }
  }

  private parseSeriesInfo(recordEl: Element, item: Partial<ItemData>): void {
    const field225 = recordEl.querySelector('datafield[tag="225"]');
    if (field225) {
      item.series = this.getSubfield(field225, 'a') || undefined;
      item.seriesNumber = this.getSubfield(field225, 'v') || undefined;
    }

    const field461 = recordEl.querySelector('datafield[tag="461"]');
    if (field461 && item.itemType === 'journalArticle') {
      item.publicationTitle = this.getSubfield(field461, 't') || undefined;
    }

    const field463 = recordEl.querySelector('datafield[tag="463"]');
    if (field463) {
      item.volume = this.getSubfield(field463, 'v') || undefined;
      item.pages = this.getSubfield(field463, 'd') || undefined;
    }
  }

  private parseLanguage(recordEl: Element, item: Partial<ItemData>): void {
    const field101 = recordEl.querySelector('datafield[tag="101"]');
    if (field101) {
      const langCode = this.getSubfield(field101, 'a');
      if (langCode) {
        item.language = mapLanguageCode(langCode);
      }
    }
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const sudocService = new SudocService();
export default sudocService;
